// Inferred teams: the contract between the team inference service
// (server/src/services/teams), its API (server/src/api/teams) and the Teams
// pages (client/src/features/teams). Server code imports these as types only.

/** How a person landed on a team. */
export type TeamMembershipSource = "inferred" | "pinned";

/** What a team was inferred from. Pinned or manual teams are "manual". */
export type TeamSignal = "jira_project" | "repo_cluster" | "review_cluster" | "manual";

export interface TeamMemberSummary {
  developerId: string;
  displayName: string;
  githubUsername: string | null;
  avatarUrl: string | null;
  isLead: boolean;
  source: TeamMembershipSource;
  /**
   * Share (0-1) of this person's work in the inference window that landed
   * in this team's JIRA project and repos. Null for pinned members with no
   * work in the window.
   */
  share: number | null;
  /** True when no single team holds at least SHARED_THRESHOLD of their work. */
  shared: boolean;
  /** Other teams this person also does real work for (share >= 0.2). */
  alsoOn: Array<{ teamId: string; name: string; share: number }>;
}

/** Period stats for one team. Same definitions as the developers roster. */
export interface TeamPeriodStats {
  headcount: number;
  /** People with code work or JIRA delivery inside the window. */
  activeCount: number;
  prsMerged: number;
  prsOpened: number;
  /** prsMerged / activeCount; null when activeCount is 0. */
  prsMergedPerPerson: number | null;
  medianHoursToMerge: number | null;
  medianHoursToFirstReview: number | null;
  /** 0-100 */
  reviewedBeforeMergeShare: number | null;
  reviewsGiven: number;
  /** Share (0-100) of the team's reviews given by its top two reviewers. */
  reviewConcentration: number | null;
  ticketsDone: number;
  pointsDone: number;
  commits: number;
  /** 0-100 share of merged PRs in each work category. */
  workMix: Array<{ category: string; label: string; share: number; count: number }>;
  /** 0-100 share of merged PRs categorized as bug fixes. */
  bugShare: number | null;
  /** 0-100 share of merged PRs flagged AI-assisted. */
  aiAssistedShare: number | null;
  /** Median contributor score (0-100) of the people with work; null with nobody. */
  score: number | null;
}

/** Working days in one window, and the holidays that took days out of it. */
export interface PeriodCalendar {
  workingDays: number;
  holidays: Array<{ name: string; date: string }>;
}

/** The whole org over one window: everyone with work, team or not. */
export interface OrgPace {
  activeCount: number;
  prsMerged: number;
  prsMergedPerPerson: number | null;
  /** Median contributor score of everyone with work. */
  score: number | null;
}

/** The norm each team is read against, for both windows. */
export interface TeamNorm {
  calendar: { current: PeriodCalendar; previous: PeriodCalendar };
  org: { current: OrgPace; previous: OrgPace };
}

export interface TeamSummary {
  id: string;
  /** Stable key, e.g. the JIRA project key ("TOPS") or "shared". */
  key: string;
  name: string;
  signal: TeamSignal;
  /**
   * Hidden teams are left out of comparisons. Listed only when the caller
   * passes includeHidden=1, so an admin can unhide them.
   */
  hidden: boolean;
  /** JIRA project key and repo full names the team was inferred from. */
  jiraProjectKey: string | null;
  repos: string[];
  leads: Array<{ developerId: string; displayName: string }>;
  current: TeamPeriodStats;
  previous: TeamPeriodStats;
}

/**
 * GET /api/teams/insights?range=30d|60d|90d[&includeHidden=1]
 * includeHidden=1 also lists hidden teams (hidden: true); they never count
 * toward orgMedian, sharedCount or unassignedCount.
 */
export interface TeamInsightsResponse {
  period: {
    period: "30d" | "60d" | "90d";
    label: string;
    prevLabel: string;
    from: string;
    to: string;
    prevFrom: string;
    prevTo: string;
  };
  /** When membership was last inferred, ISO. */
  inferredAt: string | null;
  /** Membership is inferred over this trailing window (days), not the period. */
  inferenceWindowDays: number;
  teams: TeamSummary[];
  /** Org-wide medians across teams, for comparison figures. */
  orgMedian: Pick<
    TeamPeriodStats,
    | "prsMergedPerPerson"
    | "medianHoursToMerge"
    | "medianHoursToFirstReview"
    | "reviewedBeforeMergeShare"
    | "bugShare"
  >;
  /** Reviews given by members of one team on PRs authored by another. */
  crossTeamReviews: Array<{ fromTeamId: string; toTeamId: string; count: number }>;
  /** People with work in the window who fit no team (no JIRA, no team repos). */
  unassignedCount: number;
  /** People marked shared on a non-hidden team, each counted once. */
  sharedCount: number;
  norm: TeamNorm;
}

/** GET /api/teams/insights/:teamId?range=... */
export interface TeamDetailResponse {
  period: TeamInsightsResponse["period"];
  team: TeamSummary;
  orgMedian: TeamInsightsResponse["orgMedian"];
  members: Array<
    TeamMemberSummary & {
      current: { prsMerged: number; reviewsGiven: number; ticketsDone: number; commits: number };
      previous: { prsMerged: number; reviewsGiven: number; ticketsDone: number; commits: number };
    }
  >;
  /** Weekly merged PRs for the team across current and previous windows. */
  weekly: Array<{ weekStart: string; prsMerged: number; previous: boolean }>;
  /** Teams this team reviews for and gets reviews from. */
  reviewsGivenTo: Array<{ teamId: string; name: string; count: number }>;
  reviewsReceivedFrom: Array<{ teamId: string; name: string; count: number }>;
  norm: TeamNorm;
}

/** POST /api/teams/infer  -> re-run inference now. */
export interface TeamInferenceResult {
  inferredAt: string;
  teams: number;
  assigned: number;
  shared: number;
  unassigned: number;
  /** Pinned memberships were kept as-is. */
  pinnedKept: number;
}

/** PUT /api/teams/:teamId/members/:developerId  body */
export interface PinMemberInput {
  /** Pin this person to the team (true) or release the pin (false). */
  pinned: boolean;
  isLead?: boolean;
}

/** PATCH /api/teams/:teamId  body (rename / hide) */
export interface UpdateInferredTeamInput {
  name?: string;
  /** Hidden teams are excluded from comparisons but keep their members. */
  hidden?: boolean;
}

/** Below this share of work in one team, a person is marked shared. */
export const SHARED_THRESHOLD = 0.6;

/** GET /api/teams/read?range=...[&teamId=] -> a short written read of the teams. */
export interface TeamRead {
  paragraphs: string[];
  /** Pages the read cites for industry context. */
  sources: Array<{ title: string; url: string }>;
  /** The model that wrote it; null when it was built from the numbers alone. */
  model: string | null;
  generatedAt: string;
}
